import { FacilityType, facilityTypeTree } from "@/api/facilities";
import { RootError } from "@/api/types";
import LocationSearch from "@/components/LocationSearch/LocationSearch";
import Message from "@/components/Message/Message";
import StatusTag from "@/components/StatusTag/StatusTag";
import useAnalytics from "@/hooks/useAnalytics";
import {
  useGetFacility,
  useGetFacilityContracts,
  useInsertFacility,
  useUpdateFacility,
} from "@/hooks/useFacilities";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, message, Row, Select, Space, Tooltip } from "antd";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DeleteFacilityAction from "../../DeleteFacilityAction";
import useFacilityId from "../../hooks/useFacilityId";
import { FacilityDetailsModeEnum } from "../../types";
import styles from "../FacilityDetail.module.scss";

export type FacilityDetailsMode =
  | FacilityDetailsModeEnum.validate
  | FacilityDetailsModeEnum.edit
  | FacilityDetailsModeEnum.add;

interface FacilityForm {
  address: string;
  facility_name: string;
  category: string;
  type: FacilityType;
}

export default function Details({
  mode,
  onCancel,
}: {
  mode: FacilityDetailsMode;
  onCancel?: () => void;
}) {
  const [needsSave, setNeedsSave] = useState<boolean>(false);
  const smartQueryClient = useSmartQueryClient();
  const [form] = Form.useForm<FacilityForm>();
  const facilityId = useFacilityId();
  const { data } = useGetFacility(facilityId, !isNaN(facilityId));
  const putFacility = useUpdateFacility();
  const postFacility = useInsertFacility();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const facilityData = mode === FacilityDetailsModeEnum.add ? undefined : data;
  const { data: contracts } = useGetFacilityContracts(facilityId, !isNaN(facilityId));
  const invalidAddress = facilityData?.address_is_valid === false;

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!facilityData) {
          postFacility
            .mutateAsync({
              facility: {
                address: values.address,
                facility_name: values.facility_name,
                category: values.category || null,
                type: values.type,
              },
            })
            .then((data) => {
              navigate("/facilities/" + data.data.id);
              message.success("Facility added.");
              analytics.track("Facility Added");
              setNeedsSave(false);
            })
            .catch((data: AxiosError<RootError>) => {
              if (data?.response?.data.statusCode === 400) {
                form.setFields([
                  {
                    name: "address",
                    errors: [String(data?.response.data.message)],
                  },
                ]);
                message.error(data?.response.data.message);
              } else {
                message.error("Something unexpectedly went wrong. Try again or contact us.");
              }
            });
        } else {
          putFacility
            .mutateAsync({
              facilityId: facilityId,
              facility: {
                address: values.address,
                facility_name: values.facility_name,
                category: values.category || null,
                type: values.type,
              },
            })
            .then(() => {
              onCancel?.();
              message.success("Facility saved.");
              smartQueryClient.invalidateQueries(["facility", facilityId]);
              form.setFields([
                {
                  name: "address",
                  errors: undefined,
                },
              ]);
              setNeedsSave(false);
            })
            .catch((data: AxiosError<RootError>) => {
              if (data?.response?.data.statusCode === 400) {
                form.setFields([
                  {
                    name: "address",
                    errors: [String(data?.response.data.message)],
                  },
                ]);
                message.error(data?.response.data.message);
              } else {
                message.error("Something unexpectedly went wrong. Try again or contact us.");
              }
            });
        }
      })
      .catch(() => {
        message.error("Facility could not be saved. Fix the errors first.");
      });
  };

  useEffect(() => {
    if (facilityData) {
      form.setFieldsValue({
        address: facilityData.address,
        facility_name: facilityData.facility_name,
        category: facilityData.category,
        type: facilityData.type,
      });
    }
  }, [facilityData]);

  return (
    <>
      <div className={styles.details}>
        <span className={styles.title}>Details</span>

        <Form
          form={form}
          onKeyUp={(e) => {
            if (e.code === "Enter") onSubmit();
          }}
          layout="vertical"
          name="form_in_modal"
          initialValues={{ type: "Unknown" }}
        >
          <Form.Item
            label="Facility name"
            name="facility_name"
            rules={[
              {
                required: true,
                message: "Facility name is required.",
              },
            ]}
            style={{ marginBottom: 20 }}
          >
            <Input
              placeholder={"E.g. HQ"}
              onFocus={() => setNeedsSave(true)}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 20 }}>
            <Row
              gutter={[0, 0]}
              className={invalidAddress ? styles["invalid-address"] : ""}
            >
              <Col span={24}>
                <Row
                  justify={"space-between"}
                  gutter={[0, 0]}
                  align={"middle"}
                  style={{ paddingBottom: 8 }}
                >
                  <Col>
                    <span>Address</span>
                  </Col>
                  <Col>
                    {invalidAddress && (
                      <StatusTag
                        status={"incomplete"}
                        background={"white"}
                      />
                    )}
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="address"
                  extra={
                    contracts &&
                    contracts?.total_contracts > 0 && (
                      <span className={styles.note}>
                        You can change the address, but not the country of this facility as you have
                        already added utility contracts.
                      </span>
                    )
                  }
                  style={{ marginBottom: 0 }}
                >
                  <LocationSearch onFocus={() => setNeedsSave(true)} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          {invalidAddress && facilityData.address && (
            <Message
              type={"warning"}
              style={{ marginTop: 16 }}
            >
              The address of this facility could not be validated. You can try again, or perhaps use
              just the city instead of the whole address.
            </Message>
          )}
          <Form.Item style={{ marginBottom: 20 }}>
            <Row
              gutter={[0, 8]}
              className={facilityData?.type === "Unknown" ? styles["invalid-address"] : ""}
            >
              <Col span={24}>
                <Row
                  justify={"space-between"}
                  gutter={[0, 0]}
                  align={"middle"}
                >
                  <Col>
                    <Space>
                      <div>Type</div>
                      <Tooltip title="Please classify your facilities by ownership and value chain position. We use the facility type to attribute emissions to the right scope and category following the Greenhouse Gas Protocol.">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  </Col>
                  <Col>
                    {facilityData?.type === "Unknown" && (
                      <StatusTag
                        status={"incomplete"}
                        background={"white"}
                      />
                    )}
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="type"
                  noStyle
                >
                  <Select
                    options={facilityTypeTree}
                    onFocus={() => setNeedsSave(true)}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[
              {
                max: 50,
                message: "Category name can be max. 50 characters.",
              },
            ]}
            style={{ marginBottom: 20 }}
          >
            <Input
              placeholder={"E.g. Office, supplier or warehouse"}
              onFocus={() => setNeedsSave(true)}
            />
          </Form.Item>
          <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
            {mode !== FacilityDetailsModeEnum.validate && (
              <Button
                disabled={putFacility.isLoading}
                onClick={() => navigate("/facilities")}
              >
                Cancel
              </Button>
            )}
            {mode !== FacilityDetailsModeEnum.add && (
              <DeleteFacilityAction
                onSucces={() => {
                  navigate(-1);
                }}
                facilities={facilityData ? [facilityData] : []}
              >
                <Button
                  disabled={!facilityData?.deletable}
                  danger
                >
                  <DeleteOutlined /> Delete
                </Button>
              </DeleteFacilityAction>
            )}
            <Button
              type="primary"
              onClick={() => onSubmit()}
              loading={putFacility.isLoading || postFacility.isLoading}
              disabled={(mode !== FacilityDetailsModeEnum.add && !facilityData) || !needsSave}
            >
              Save
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}
